import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import { TALKING_POINTS_IN_FLIGHT_STATUSES } from "@/enums";
import {
  completeCall,
  fetchPrepForSource,
  updatePrep,
} from "@/services/frontendServices/prepServices";
import type { MeetingPrepDto, PrepSource, UpdatePrepPayload } from "@/types";

import { accountPrepKeys } from "./useAccountPrep";
import { activityKeys } from "./useActivities";
import { myScheduleKeys } from "./useMySchedule";

export const prepKeys = {
  all: ["prep"] as const,
  detail: (prepSourceId: string) => [...prepKeys.all, prepSourceId] as const,
};

/** Fast enough that "press Refresh and watch" feels live, slow enough to be cheap. */
const TALKING_POINTS_POLL_MS = 5000;

/** One cache identity per prep view; ad hoc keys are namespaced to avoid ever
 * colliding with an activity id. */
export function prepSourceKey(source: PrepSource): string {
  return source.kind === "activity"
    ? source.activityId
    : `adhoc:${source.prepId}`;
}

export const usePrep = (
  source: PrepSource,
  options?: Partial<UseQueryOptions<MeetingPrepDto, Error>>
) =>
  useQuery<MeetingPrepDto, Error>({
    queryKey: prepKeys.detail(prepSourceKey(source)),
    queryFn: () => fetchPrepForSource(source),
    enabled: true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    // Opening a prep always re-reads it, cache or no cache. `sourcesChanged` is
    // computed server-side on every prep read and has no other way in: a cached
    // prep answers "nothing moved" with a value that predates whatever moved.
    // Within the staleTime window — the account page is one click away, so this
    // is the common path, not the corner — the rep would create a signal, open
    // the prep, and be told its points are current.
    //
    // Scoped to mount rather than done by invalidating from every source write:
    // a source moves for reasons the client never sees (CRM and Gong sync, the
    // bg-agent, another rep), so only re-reading catches them all. It also keeps
    // the refetch off an open prep, where replacing the whole DTO mid-call could
    // walk over a check the rep just ticked.
    refetchOnMount: "always",
    // Generation is handed to the bg-agent, which never calls back — the only
    // way the rep sees the result is this poll. It also has to run on mount, not
    // just after Refresh: the warm-up sweep requests generation hours before
    // anyone opens the prep, so an in-flight run is the common case on arrival.
    refetchInterval: (query) =>
      query.state.data &&
      TALKING_POINTS_IN_FLIGHT_STATUSES.includes(
        query.state.data.talkingPointsStatus
      )
        ? TALKING_POINTS_POLL_MS
        : false,
    ...options,
  });

/**
 * A mutation response is authoritative for what its own request changed, never
 * for the whole prep. Every prep endpoint re-reads the row to build its reply,
 * so a reply whose read predates another writer's commit carries that writer's
 * *old* value — and adopting it wholesale reverts a check the rep just ticked
 * or a goal they just saved, in the UI, mid-call. Each writer therefore merges
 * only the fields it owns and leaves the rest of the cached prep alone.
 *
 * With nothing cached there is no other writer's value to protect, so the
 * response is adopted whole.
 */
function mergeOwnedFields<K extends keyof MeetingPrepDto>(
  cached: MeetingPrepDto | undefined,
  response: MeetingPrepDto,
  owned: readonly K[]
): MeetingPrepDto {
  if (!cached) return response;
  return owned.reduce<MeetingPrepDto>(
    (prep, field) => ({ ...prep, [field]: response[field] }),
    cached
  );
}

/**
 * What PATCH /api/preps/[id] actually writes, keyed by the payload field that
 * asks for it. `goalOrigin` rides with `goalText` because the server derives
 * one from the other, and `status` rides with `action` — the client never
 * sends a status, but a save that carries an action moves it server-side, so
 * that reply owns it. A save without an action owns no status at all, and must
 * not carry one back over a Complete Prep that landed first.
 *
 * Keyed by `keyof UpdatePrepPayload` on purpose: a field added to
 * `updatePrepSchema` without a line here is a compile error rather than a
 * response value silently dropped from the cache — stale UI with no error and
 * no failing test, the exact shape of bug this merge exists to prevent.
 */
const FIELDS_OWNED_BY_UPDATE: Record<
  keyof UpdatePrepPayload,
  readonly (keyof MeetingPrepDto)[]
> = {
  goalText: ["goalText", "goalOrigin"],
  quickNotes: ["quickNotes"],
  action: ["status"],
};

function fieldsOwnedByUpdate(
  payload: UpdatePrepPayload
): readonly (keyof MeetingPrepDto)[] {
  const asked = Object.keys(
    FIELDS_OWNED_BY_UPDATE
  ) as (keyof UpdatePrepPayload)[];
  return asked.flatMap((field) =>
    payload[field] !== undefined ? FIELDS_OWNED_BY_UPDATE[field] : []
  );
}

/**
 * Every prep write invalidates the schedule too: the dashboard CTA is derived
 * from prepState, and it has to follow a save without a manual refresh. It also
 * invalidates the account prep summary, which drives the account page's Start
 * Prep button and would otherwise show a stale status after a save.
 */
export const useUpdatePrep = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingPrepDto,
    Error,
    { prepId: string; payload: UpdatePrepPayload }
  >({
    mutationFn: ({ prepId, payload }) => updatePrep(prepId, payload),
    onSuccess: (prep, { payload }) => {
      queryClient.setQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source)),
        (cached) => mergeOwnedFields(cached, prep, fieldsOwnedByUpdate(payload))
      );
      queryClient.invalidateQueries({ queryKey: myScheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: accountPrepKeys.all });
    },
  });
};

/**
 * The one write that cannot be undone. Invalidates the same surfaces a save does
 * — the schedule row turns into its completed state and the account's Start Prep
 * button loses the ad hoc slot this prep was holding.
 *
 * The only writer that still adopts the response whole, and the only one for
 * which "authoritative for the entire prep" is literally true: the caller
 * drains every outstanding write before dispatching it (the notes flush gates
 * the freeze, the checklist flush is awaited, a dirty prep is saved first), and
 * once it lands the row is frozen and every other endpoint answers 409. There
 * is no newer write for a stale field to revert, and no later one to correct
 * it, so what the freeze read is the final state of the prep.
 */
export const useCompleteCall = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<MeetingPrepDto, Error, { prepId: string }>({
    mutationFn: ({ prepId }) => completeCall(prepId),
    onSuccess: (prep) => {
      queryClient.setQueryData(prepKeys.detail(prepSourceKey(source)), prep);
      queryClient.invalidateQueries({ queryKey: myScheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: accountPrepKeys.all });
      // The Brief keeps Upcoming and Past under this key; the meeting has to
      // change section while the account page is still mounted under the Prep.
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
    },
  });
};
